/**
 * Which tool is active, and what its options are.
 *
 * Brush and eraser are live; the rest are declared and not built.
 *
 * The registry earned itself immediately: adding the eraser was a table entry
 * and a flag, because the composite mode already flows from here through
 * `strokeCompositeMode` to both the preview and the commit. Wired directly into
 * the canvas's pointer handlers instead, it would have been another branch in
 * `wirePainting` and a second place for the two paths to disagree.
 *
 * The shapes here come from docs/decisions/paint-features.md and
 * docs/decisions/interaction-and-devices.md. Two of them are worth restating
 * because they are decisions rather than description:
 *
 *   **The eraser is a brush mode, not a tool with its own settings.** Giving it
 *   a separate size and opacity copies Photoshop's history rather than making a
 *   choice; people expect the eraser to be the brush, inverted.
 *
 *   **Fill has two behaviours behind one explicit switch**, never a heuristic
 *   that guesses which you meant. "Whole face" is the default because it is the
 *   one no other editor can offer.
 */
import type { RegionId } from "../../shared/format";

export type ToolId = "brush" | "eraser" | "faces" | "eyedropper" | "fill" | "pan";

export interface BrushOptions {
  /** Template pixels. */
  size: number;
  /** 0–1, applied once when the stroke commits — see paint.ts. */
  opacity: number;
  /**
   * 0 = soft edge, 1 = hard.
   *
   * Not a blur on the outline, which was the original guess here — below 1 the
   * painter switches to stamping a gradient tip along the stroke, because a
   * blurred outline softens the ends into a smear and cannot build up. See
   * paint.ts and docs/decisions/soft-brush.md.
   */
  hardness: number;
  /**
   * 0..1, coverage laid down over one brush diameter of travel.
   *
   * Only meaningful below hardness 1: the hard path is one filled outline with
   * nothing to accumulate. This is the control that makes shading possible —
   * a low flow darkens gradually as you pass over an area again.
   */
  flow: number;
}

export type FillMode =
  /** One face of the template. Ours; no general editor has the region map. */
  | "face"
  /**
   * Every face of the body part under the pointer.
   *
   * A sleeve is four faces plus two caps, and "make this sleeve navy" was six
   * taps and six things to undo. Finer than the part *group*, deliberately:
   * both arms share a group, and a garment with different sleeves is something
   * people make on purpose.
   */
  | "part"
  /** Classic flood fill from the sampled colour. */
  | "colour";

export interface FillOptions {
  mode: FillMode;
  /** 0–255. Only meaningful for `colour`; antialiased art needs some slack. */
  tolerance: number;
}

export interface ToolState {
  active: ToolId;
  brush: BrushOptions;
  fill: FillOptions;
  /** Set while the eyedropper is sampling, for the touch loupe. */
  sampling: RegionId | null;
}

export const DEFAULT_TOOLS: ToolState = {
  active: "brush",
  // Hard by default: the brush anyone opens the app with should behave like
  // the brush they expect, and soft is a deliberate choice.
  brush: { size: 24, opacity: 1, hardness: 1, flow: 0.5 },
  fill: { mode: "face", tolerance: 24 },
  sampling: null,
};

/**
 * What each tool is called and what it needs from the app.
 *
 * `implemented` is load-bearing rather than documentation: the rail renders
 * only implemented tools. Ten buttons where one worked is what the editor had
 * before, and a control that does nothing spends trust the product has not
 * earned yet — so a tool appears when it works and not before.
 */
export interface ToolMeta {
  id: ToolId;
  /** Matches an icon name in shared/icons.ts. */
  icon: string;
  label: string;
  /** One line, shown in the tool's options popover. */
  hint: string;
  implemented: boolean;
}

export const TOOLS: readonly ToolMeta[] = [
  {
    id: "brush",
    icon: "brush",
    label: "Brush",
    hint: "Draw on the template. One finger draws, two fingers move.",
    implemented: true,
  },
  {
    id: "eraser",
    icon: "eraser",
    label: "Eraser",
    hint: "Rub paint away. Same size and opacity as the brush.",
    implemented: true,
  },
  {
    id: "faces",
    icon: "faces",
    label: "Select faces",
    hint: "Choose which faces to paint on. Tap a face, or switch to Part.",
    implemented: true,
  },
  {
    id: "eyedropper",
    icon: "eyedropper",
    label: "Pick colour",
    hint: "Tap the canvas to take a colour from it, then carry on where you were.",
    implemented: true,
  },
  {
    id: "fill",
    icon: "fill",
    label: "Fill",
    hint: "Tap a face to fill it, or switch to matching colour.",
    implemented: true,
  },
  {
    id: "pan",
    icon: "pan",
    label: "Move",
    hint: "Drag to move the canvas. Two fingers does this anywhere.",
    implemented: true,
  },
];

export type ToolListener = (state: Readonly<ToolState>) => void;

export function createToolState(initial: Partial<ToolState> = {}) {
  let state: ToolState = {
    ...DEFAULT_TOOLS,
    ...initial,
    brush: { ...DEFAULT_TOOLS.brush, ...initial.brush },
    fill: { ...DEFAULT_TOOLS.fill, ...initial.fill },
  };
  const listeners = new Set<ToolListener>();
  const emit = () => {
    for (const l of listeners) l(state);
  };

  return {
    get(): Readonly<ToolState> {
      return state;
    },

    /** Selecting an unimplemented tool is a no-op rather than a broken mode. */
    select(id: ToolId): boolean {
      const meta = TOOLS.find((t) => t.id === id);
      if (!meta?.implemented || state.active === id) return false;
      state = { ...state, active: id };
      emit();
      return true;
    },

    setBrush(patch: Partial<BrushOptions>) {
      state = { ...state, brush: { ...state.brush, ...patch } };
      emit();
    },

    setFill(patch: Partial<FillOptions>) {
      state = { ...state, fill: { ...state.fill, ...patch } };
      emit();
    },

    subscribe(fn: ToolListener) {
      listeners.add(fn);
      fn(state);
      return () => listeners.delete(fn);
    },
  };
}

export type Tools = ReturnType<typeof createToolState>;
